<template>
  <div class="api-key-container">
    <div class="header">
      <div class="title-section">
        <div class="title">{{ $t('workspaceSettings.apiKey.title') }}</div>
        <div class="top-tip text-gray-500">
          {{ $t('workspaceSettings.apiKey.topTip') }}
        </div>
      </div>
      <a-button type="primary" @click="openCreateModal">
        <template #icon><icon-plus /></template>
        {{ $t('workspaceSettings.apiKey.create') }}
      </a-button>
    </div>
    <a-table
      :loading="loading"
      :data="dataList"
      :pagination="pagination"
      :bordered="{ cell: false }"
      :stripe="true"
      @page-change="onPageChange"
    >
      <template #columns>
        <a-table-column
          :title="$t('workspaceSettings.apiKey.columns.name')"
          data-index="name"
        >
          <template #cell="{ record }">
            <div class="name-text">{{ record.name }}</div>
          </template>
        </a-table-column>

        <a-table-column
          :title="$t('workspaceSettings.apiKey.columns.description')"
          data-index="description"
        >
          <template #cell="{ record }">
            {{ record.description || '-' }}
          </template>
        </a-table-column>
        <a-table-column
          :title="$t('workspaceSettings.apiKey.columns.apiKey')"
          data-index="masked_key"
        />
        <a-table-column
          :title="$t('workspaceSettings.apiKey.columns.createdAt')"
          data-index="created_at"
        >
          <template #cell="{ record }">
            {{ dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss') || '-' }}
          </template>
        </a-table-column>
        <a-table-column
          :title="$t('workspaceSettings.apiKey.columns.lastUsed')"
          data-index="last_used_at"
        >
          <template #cell="{ record }">
            {{
              record.last_used_at
                ? dayjs(record.last_used_at).format('YYYY-MM-DD HH:mm:ss')
                : $t('workspaceSettings.apiKey.columns.never')
            }}
          </template>
        </a-table-column>
        <a-table-column
          :title="$t('workspaceSettings.apiKey.columns.actions')"
          :width="150"
        >
          <template #cell="{ record }">
            <a-space size="small">
              <a-button type="text" size="small" @click="openEditModal(record)">
                <template #icon><icon-edit /></template>
              </a-button>
              <a-button type="text" size="small" @click="handleCopyKey(record)">
                <template #icon><icon-copy /></template>
              </a-button>
              <a-button
                type="text"
                status="danger"
                size="small"
                @click="confirmDelete(record)"
              >
                <template #icon><icon-delete /></template>
              </a-button>
            </a-space>
          </template>
        </a-table-column>
      </template>
    </a-table>

    <!-- Create/Edit Modal -->
    <a-modal
      v-model:visible="modalVisible"
      :title="
        isEdit
          ? $t('workspaceSettings.apiKey.modal.editTitle')
          : $t('workspaceSettings.apiKey.modal.createTitle')
      "
      :ok-loading="modalLoading"
      :ok-text="$t('workspaceSettings.apiKey.modal.save')"
      :cancel-text="$t('workspaceSettings.apiKey.modal.cancel')"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    >
      <a-form ref="formRef" :model="formData" layout="vertical">
        <a-form-item
          field="name"
          :label="$t('workspaceSettings.apiKey.modal.name')"
          :rules="[
            {
              required: isEdit,
              message: $t('workspaceSettings.apiKey.modal.required'),
            },
          ]"
        >
          <a-input
            v-model="formData.name"
            :placeholder="
              isEdit
                ? $t('workspaceSettings.apiKey.modal.namePlaceholder')
                : $t('workspaceSettings.apiKey.modal.autoGenerated')
            "
          />
        </a-form-item>
        <a-form-item
          field="description"
          :label="$t('workspaceSettings.apiKey.modal.description')"
        >
          <a-textarea
            v-model="formData.description"
            :placeholder="
              $t('workspaceSettings.apiKey.modal.descriptionPlaceholder')
            "
            :auto-size="{ minRows: 3, maxRows: 5 }"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Secret Display Modal -->
    <a-modal
      v-model:visible="secretVisible"
      :title="$t('workspaceSettings.apiKey.modal.secretTitle')"
      :footer="false"
      simple
    >
      <div class="mb-4 text-gray-600">
        {{ $t('workspaceSettings.apiKey.modal.secretNotice') }}
      </div>
      <div
        class="bg-gray-50 p-3 rounded border mb-6 break-all font-mono text-sm"
      >
        {{ newApiKey }}
      </div>
      <div class="flex justify-end">
        <a-button type="primary" @click="copyToClipboard(newApiKey)">
          {{ $t('workspaceSettings.apiKey.modal.copy') }}
        </a-button>
        <a-button type="primary" @click="secretVisible = false">
          {{ $t('workspaceSettings.apiKey.modal.close') }}
        </a-button>
      </div>
    </a-modal>

    <!-- Delete Modal -->
    <a-modal v-model:visible="deleteModalVisible" :footer="false" simple>
      <div
        class="delete-modal-title mb-4 flex items-center gap-2 text-lg font-bold text-gray-900"
      >
        <icon-exclamation-circle-fill class="text-red-500 text-xl" />
        <span>{{
          $t('workspaceSettings.apiKey.deleteConfirm.title', {
            name: deleteKey?.name,
          })
        }}</span>
      </div>
      <div class="delete-modal-content mb-6 text-gray-700">
        <div v-if="deleteKey?.last_used_at">
          {{ $t('workspaceSettings.apiKey.deleteConfirm.contentUsedPrefix') }}
          <span class="text-red-500 font-medium">{{
            deleteKey.last_used_at
          }}</span>
          {{ $t('workspaceSettings.apiKey.deleteConfirm.contentSuffix') }}
        </div>
        <div v-else>
          {{ $t('workspaceSettings.apiKey.deleteConfirm.contentUnused') }}
        </div>
      </div>
      <div class="flex justify-end gap-3">
        <a-button
          type="dashed"
          status="danger"
          :loading="deleteLoading"
          @click="executeDelete"
        >
          {{ $t('workspaceSettings.apiKey.deleteConfirm.confirm') }}
        </a-button>
        <a-button @click="deleteModalVisible = false">
          {{ $t('workspaceSettings.apiKey.deleteConfirm.cancel') }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
  import { ref, reactive, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Message from '@/utils/message';
  import { useClipboard } from '@vueuse/core';
  import {
    IconEdit,
    IconCopy,
    IconDelete,
    IconPlus,
    IconExclamationCircleFill,
  } from '@arco-design/web-vue/es/icon';
  import { useUserStore } from '@/store';
  import {
    listApiKeyApi,
    createApiKeyApi,
    updateApiKeyApi,
    deleteApiKeyApi,
    getApiKeySecretApi,
  } from '@/api/api-key';
  import { ApiKey } from '@/types/api-key';
  import dayjs from 'dayjs';

  const { t } = useI18n();
  const { copy } = useClipboard();
  const userStore = useUserStore();

  const loading = ref(false);
  const dataList = ref<ApiKey[]>([]);
  const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
    showTotal: true,
  });

  const modalVisible = ref(false);
  const modalLoading = ref(false);
  const isEdit = ref(false);
  const formRef = ref();
  const formData = reactive({
    name: '',
    description: '',
    api_key_uuid: '',
  });

  const secretVisible = ref(false);
  const newApiKey = ref('');

  const deleteModalVisible = ref(false);
  const deleteLoading = ref(false);
  const deleteKey = ref<ApiKey | null>(null);

  const fetchList = async (page = 1) => {
    loading.value = true;
    try {
      const res = await listApiKeyApi({
        pagination: {
          page,
          page_size: pagination.pageSize,
        },
        filters: {
          tenant_uuid: userStore.tenantUuid || '',
          workspace_uuid: userStore.workspaceUuid || '',
        },
      });
      //  @ts-ignore
      if (res.code === 0) {
        dataList.value = res.data.results;
        pagination.total = res.data.total_count;
        pagination.current = page;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      loading.value = false;
    }
  };

  const onPageChange = (page: number) => {
    fetchList(page);
  };

  onMounted(() => {
    fetchList();
  });

  // Create
  const openCreateModal = () => {
    isEdit.value = false;
    formData.name = '';
    formData.description = '';
    formData.api_key_uuid = '';
    modalVisible.value = true;
  };

  // Edit
  const openEditModal = (record: ApiKey) => {
    isEdit.value = true;
    formData.name = record.name;
    formData.description = record.description;
    formData.api_key_uuid = record.api_key_uuid;
    modalVisible.value = true;
  };

  const handleModalOk = async () => {
    const validation = await formRef.value?.validate();
    if (validation) return;

    modalLoading.value = true;
    try {
      if (isEdit.value) {
        await updateApiKeyApi({
          name: formData.name,
          description: formData.description,
          api_key_uuid: formData.api_key_uuid,
          tenant_uuid: userStore.tenantUuid || '',
          workspace_uuid: userStore.workspaceUuid || '',
        });
        Message.success(t('workspaceSettings.workspace.updateSuccess'));
        modalVisible.value = false;
        fetchList(pagination.current);
      } else {
        const res = await createApiKeyApi({
          description: formData.description,
          tenant_uuid: userStore.tenantUuid || '',
          workspace_uuid: userStore.workspaceUuid || '',
          runtime_resource: 'api', // Default value
        });
        //  @ts-ignore
        if (res.code === 0) {
          newApiKey.value = res.data.api_key || '';
          secretVisible.value = true;
          modalVisible.value = false;
          Message.success(t('workspaceSettings.apiKey.modal.createSuccess'));
          fetchList();
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      modalLoading.value = false;
    }
  };

  const handleModalCancel = () => {
    modalVisible.value = false;
  };

  // Copy Secret
  const copyToClipboard = async (text: string) => {
    await copy(text);
    Message.success(t('workspaceSettings.apiKey.modal.copySuccess'));
  };

  const handleCopyKey = async (record: ApiKey) => {
    try {
      const res = await getApiKeySecretApi({
        tenant_uuid: userStore.tenantUuid || '',
        workspace_uuid: userStore.workspaceUuid || '',
        api_key_uuid: record.api_key_uuid,
      });
      // @ts-ignore
      if (res.code === 0 && res.data.api_key) {
        copyToClipboard(res.data.api_key);
        Message.success(t('workspaceSettings.apiKey.modal.copySuccess'));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  // Delete
  const confirmDelete = (record: ApiKey) => {
    deleteKey.value = record;
    deleteModalVisible.value = true;
  };

  const executeDelete = async () => {
    if (!deleteKey.value) return;
    deleteLoading.value = true;
    try {
      await deleteApiKeyApi({
        tenant_uuid: userStore.tenantUuid || '',
        workspace_uuid: userStore.workspaceUuid || '',
        api_key_uuid: deleteKey.value.api_key_uuid,
      });
      Message.success(t('workspaceSettings.apiKey.actions.deleteSuccess'));
      deleteModalVisible.value = false;
      fetchList(pagination.current);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      deleteLoading.value = false;
    }
  };
</script>

<style lang="less" scoped>
  .api-key-container {
    background: #fff;

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 24px;

      .title-section {
        .title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 8px;
          color: var(--color-text-1);
        }
        .top-tip {
          font-size: 14px;
          color: var(--color-text-3);
        }
      }
    }

    .name-text {
      font-weight: 500;
      color: var(--color-text-1);
    }
  }
</style>
